import type { InteractionResponse } from '@vagusx/clients';

/**
 * Wall-clock instant of a recording's FIRST SAMPLE — the anchor every
 * transcript offset is measured from — or null when the call has none.
 *
 * ONE SOURCE ONLY: `recordingStartedAtMs`. For calls recorded after ADR-206
 * this is `FileInfo.startedAt`, written by the egress webhook: the exact first
 * sample. For older calls it was backfilled from the GCS object's
 * `timeCreated - duration`, which is the same instant plus upload time.
 *
 * `ended_at - recordingDurationSeconds` was a second source and is GONE. It
 * looked reasonable and was wrong: measured against when egress actually
 * finished writing each file, it runs late by a bimodal ~0.6s or ~5.7s
 * (median 0.80s, max 5.85s). On int `28d8834f` it placed the caller's opening
 * "Hello?" 4.2 seconds BEFORE the recording began, which is impossible, and
 * nothing in the UI could have revealed it. A fallback that is confidently
 * several seconds wrong is worse than no highlight, because the reader has no
 * way to tell which they are looking at.
 *
 * Null therefore means null: no ring, no click-to-seek. Playback and the
 * scrubber are unaffected.
 */
export function resolveRecordingAnchor(
  interaction: Pick<InteractionResponse, 'recordingStartedAtMs'>
): string | null {
  const { recordingStartedAtMs } = interaction;
  if (recordingStartedAtMs == null || !Number.isFinite(Number(recordingStartedAtMs))) {
    return null;
  }
  // Coerced: Postgres `bigint` reaches JSON as a string through some drivers,
  // and `Number.isFinite` does not coerce — an un-coerced guard would drop a
  // perfectly good anchor and silently disable alignment.
  const d = new Date(Number(recordingStartedAtMs));
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
